import { defineStore } from 'pinia'
import { markRaw } from 'vue'
import * as THREE from 'three'

// Reusable object to avoid allocation during updates
const _dummy = new THREE.Object3D()

export const useMapPortalStore = defineStore('mapPortalStore', {
  state: () => ({
    portalInstancedMesh: null as THREE.InstancedMesh<
      THREE.PlaneGeometry,
      THREE.ShaderMaterial
    > | null,
    portalAvailableInstanceIds: markRaw<number[]>([]),
    portalMapCoordinateLookup: markRaw(new Map())
  }),
  actions: {
    initPortalInstancedMesh(maxCount: number) {
      const geometry = new THREE.PlaneGeometry(1, 1)

      // Load the portal texture
      const textureLoader = new THREE.TextureLoader()
      const portalTexture = textureLoader.load('https://cdn.ferion.com/images/map/blackhole.webp')

      const material = new THREE.ShaderMaterial({
        uniforms: {
          portalTexture: { value: portalTexture },
          time: { value: 0.0 }
        },
        vertexShader: `
            varying vec2 vUv;

            void main() {
              vUv = uv;
              vec4 mvPosition = modelViewMatrix * instanceMatrix * vec4(position, 1.0);
              gl_Position = projectionMatrix * mvPosition;
            }
        `,
        fragmentShader: `
            uniform sampler2D portalTexture;
            uniform float time;
            varying vec2 vUv;

            void main() {
              // Center UVs for rotation
              vec2 centeredUV = vUv - 0.5;

              // Rotate UVs
              float angle = time * 0.3;
              float s = sin(angle);
              float c = cos(angle);
              vec2 rotatedUV = vec2(
                centeredUV.x * c - centeredUV.y * s,
                centeredUV.x * s + centeredUV.y * c
              );

              // Bring UVs back to 0-1 range
              rotatedUV += 0.5;

              vec4 textureColor = texture2D(portalTexture, rotatedUV);
              gl_FragColor = textureColor;
            }
        `,
        transparent: true
      })

      // Initialize the InstancedMesh
      this.portalInstancedMesh = markRaw(new THREE.InstancedMesh(geometry, material, maxCount))
      this.portalInstancedMesh.name = 'portal'
      this.portalInstancedMesh.frustumCulled = false // Disable frustum culling to prevent disappearing on large maps

      const dummy = new THREE.Object3D()
      // Setup initial positions
      for (let i = 0; i < maxCount; ++i) {
        // Set dummy position out of range
        dummy.position.set(-10000, -10000, -10000)
        dummy.updateMatrix()
        this.portalInstancedMesh.setMatrixAt(i, dummy.matrix)

        this.portalAvailableInstanceIds.push(i)
      }

      this.portalInstancedMesh.instanceMatrix.needsUpdate = true

      return this.portalInstancedMesh
    },
    addPortal(x: number, y: number): number {
      if (this.portalInstancedMesh) {
        const instanceId = this.getAvailablePortalInstanceId()
        if (instanceId !== null && instanceId !== undefined) {
          _dummy.position.set(x, y, 0)
          _dummy.scale.set(10, 10, 10)
          _dummy.updateMatrix()

          this.portalInstancedMesh.setMatrixAt(instanceId, _dummy.matrix)
          this.portalInstancedMesh.instanceMatrix.needsUpdate = true

          this.portalMapCoordinateLookup.set(instanceId, { x: x, y: y })

          return instanceId
        }
      }

      return 0
    },
    removePortal(instanceId: number) {
      if (this.portalInstancedMesh) {
        _dummy.position.set(10000, 10000, 10000)
        _dummy.scale.set(1, 1, 1) // Reset scale
        _dummy.updateMatrix()
        this.portalInstancedMesh.setMatrixAt(instanceId, _dummy.matrix)
        this.releasePortalInstanceId(instanceId)
        this.portalInstancedMesh.instanceMatrix.needsUpdate = true
      }
    },
    getAvailablePortalInstanceId(): number {
      if (this.portalAvailableInstanceIds.length === 0) {
        throw new Error('No more available instance ids for portal objects')
      }
      return this.portalAvailableInstanceIds.pop()!
    },
    releasePortalInstanceId(instanceId: number) {
      this.portalAvailableInstanceIds.push(instanceId)
      this.portalMapCoordinateLookup.delete(instanceId)
    },
    findCoordinatesByInstanceId(instanceId: number): { x: number; y: number } {
      if (this.portalMapCoordinateLookup.has(instanceId)) {
        return this.portalMapCoordinateLookup.get(instanceId)
      }
      return { x: 0, y: 0 }
    },
    findInstanceIdByCoordinates(x: number, y: number): number {
      for (let i = 0; i < this.portalInstancedMesh!.count; ++i) {
        const coordinates = this.findCoordinatesByInstanceId(i)
        if (coordinates.x === x && coordinates.y === y) {
          return i
        }
      }
      return -1
    }
  }
})
